import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import styled from 'styled-components'

import { COLORS } from '../commons/Colors'
import CheckboxComponent from '../commons/components/CheckboxComponent'
import DownloadFile from '../commons/components/DownloadFile'
import RoundTagComponent from '../commons/components/RoundTagComponent'
import { INotification } from '../models/INotification'
import { getFileNameByPath } from './utils/FileUtils'

export type NotificationCardComponentProps = {
    notification: INotification
    onEdit: (notification: INotification) => void
    onDelete: (id: string) => void
    onUnpublish: (notification: INotification) => void
    isCardSelected: (id: string) => boolean
    handleCardSelection: (id: string) => void
    downloadFile: (path: string) => void
}

function NotificationCardComponent({
    notification,
    onEdit,
    onDelete,
    onUnpublish,
    isCardSelected,
    downloadFile,
    handleCardSelection,
}: NotificationCardComponentProps): JSX.Element {
    const isAppSource = (): boolean => notification?.source?.name === 'App'

    const renderSourceIcon = (): JSX.Element => {
        if (isAppSource()) {
            return (
                <RoundTagComponent
                    text="App"
                    color={COLORS.primaryBlue}
                    small
                />
            )
        }

        return (
            <RoundTagComponent
                text="Portal"
                color={COLORS.portalRedTag}
                small
            />
        )
    }

    const renderStatusIcon = (): JSX.Element => {
        if (notification.published) {
            return (
                <RoundTagComponent
                    text="Publicado"
                    color={`${COLORS.disabledButton}`}
                />
            )
        }

        return <RoundTagComponent text="Salvo" color={`${COLORS.success}`} />
    }

    const getIconColor = () =>
        notification.published ? COLORS.disabledText : COLORS.primaryGray

    return (
        <CardContainer
            published={notification.published}
            data-testid="notification-card"
        >
            <CardHeader>
                {!notification.published && (
                    <CheckboxComponent
                        checked={isCardSelected(notification.id!)}
                        onChange={() => handleCardSelection(notification.id!)}
                        value={notification.id!}
                    />
                )}
                <NotificationName>{notification.name}</NotificationName>
                <NotificationSourceTag>
                    {notification?.source?.name !== 'Gdv' && renderSourceIcon()}
                </NotificationSourceTag>
            </CardHeader>
            <CardBody>
                <NotificationDescription>
                    {notification.description}
                </NotificationDescription>
                {notification.files && notification.files.length > 0 && (
                    <FilesContainer>
                        {notification.files.map((file) => (
                            <FileItem key={file.id}>
                                <DownloadFile
                                    name={getFileNameByPath(file.path)!}
                                    onPress={() => downloadFile(file.path)}
                                />
                            </FileItem>
                        ))}
                    </FilesContainer>
                )}
            </CardBody>
            <CardFooter>
                {notification.published && (
                    <UnpublishButton onClick={() => onUnpublish(notification)}>
                        Inativar Publicação
                    </UnpublishButton>
                )}

                <NotificationStatusTag>
                    {renderStatusIcon()}
                </NotificationStatusTag>
                <EditionIconButton
                    data-testid="edit-icon-button"
                    onClick={() => onEdit(notification)}
                    published={notification.published}
                    disabled={notification.published}
                >
                    <EditIcon sx={{ color: `${getIconColor()}` }} />
                </EditionIconButton>
                <DeleteIconButton
                    data-testid="delete-icon-button"
                    onClick={() => onDelete(notification.id!)}
                    published={notification.published}
                    disabled={notification.published}
                >
                    <DeleteIcon sx={{ color: `${getIconColor()}` }} />
                </DeleteIconButton>
            </CardFooter>
        </CardContainer>
    )
}

interface ICardContainerProps {
    published?: boolean
}

const CardContainer = styled.div<ICardContainerProps>`
    display: flex;
    flex-direction: column;
    border: 1px solid ${COLORS.borderGray};
    background-color: ${({ published }) =>
        published ? COLORS.borderGray : COLORS.primaryWhite};
    font-family: Roboto;
    font-size: 12px;
    text-transform: uppercase;
    border-radius: 3px;
    padding: 24px 34px 24px 10px;
`

const CardHeader = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
`

const NotificationName = styled.span`
    font-family: Roboto;
    font-weight: bold;
    font-size: 12px;
    color: ${COLORS.primaryGray};
`
const NotificationSourceTag = styled.div`
    margin-left: 5px;
`

const CardBody = styled.div`
    display: flex;
    flex-direction: column;
    margin-top: 24px;
`

const NotificationDescription = styled.span`
    font-family: Roboto;
    font-size: 12px;
    line-height: 2;
    letter-spacing: 0.09px;
    color: ${COLORS.primaryGray};
`

const FilesContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 16px;
`

const FileItem = styled.div`
    margin-bottom: 8px;
`

const CardFooter = styled.div`
    display: flex;
    flex-direction: row;
    align-self: flex-end;
    align-items: center;
`

const NotificationStatusTag = styled.div``
const EditionIconButton = styled.button<ICardContainerProps>`
    cursor: ${({ published }) => (published ? 'default' : 'pointer')};
    border: none;
    background-color: transparent;
    margin-left: 12px;
`
const DeleteIconButton = styled(EditionIconButton)``

const UnpublishButton = styled.button`
    cursor: pointer;
    border: none;
    background: none;
    margin-right: 12px;
    min-width: 68px;
    min-height: 19px;
    border-radius: 24px;
    border-width: none;
    border: none;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
    font-family: 'Roboto';
    font-size: 9px;
    background-color: ${COLORS.error};
    color: ${COLORS.primaryWhite};
`

export default NotificationCardComponent
